import type { ResolvedLocation } from "@/lib/location/types"

type MapboxContext = {
  id: string
  text: string
  short_code?: string
}

type MapboxFeature = {
  id: string
  place_name: string
  text: string
  center: [number, number]
  context?: MapboxContext[]
}

function parseMapboxContext(
  context: MapboxContext[] | undefined,
): Pick<
  ResolvedLocation,
  "city" | "state" | "postalCode" | "country"
> {
  let city: string | undefined
  let state: string | undefined
  let postalCode: string | undefined
  let country: string | undefined
  for (const c of context ?? []) {
    const prefix = c.id.split(".")[0] ?? ""
    const t = c.text?.trim()
    if (!t) continue
    if (prefix === "postcode") postalCode = t
    else if (prefix === "place" || prefix === "district") city = city ?? t
    else if (prefix === "region")
      state = (c.short_code ?? t).replace(/^US-/i, "")
    else if (prefix === "country") country = t
  }
  return { city, state, postalCode, country }
}

function featureToResolved(f: MapboxFeature): ResolvedLocation | null {
  const [lng, lat] = f.center
  if (typeof lat !== "number" || typeof lng !== "number") return null
  const placeName = f.place_name.trim()
  const firstSeg = placeName.split(",")[0]?.trim() ?? ""
  const fromText = f.text?.trim() ?? ""
  // US-style addresses: `text` is often the route without the house number; first segment of
  // `place_name` includes the full street line.
  const locationName =
    /^\d/.test(firstSeg) && !/^\d/.test(fromText)
      ? firstSeg
      : fromText || firstSeg
  if (!locationName) return null
  const { city, state, postalCode, country } = parseMapboxContext(f.context)
  return {
    locationName,
    address: placeName,
    lat,
    lng,
    placeId: f.id,
    city,
    state,
    postalCode,
    country,
  }
}

/**
 * Forward geocode suggestions (Mapbox Geocoding API). Call from the browser only;
 * requires `NEXT_PUBLIC_MAPBOX_PUBLIC_TOKEN`.
 */
export async function fetchGeocodeSuggestions(
  query: string,
  opts?: { signal?: AbortSignal },
): Promise<ResolvedLocation[]> {
  const token = process.env.NEXT_PUBLIC_MAPBOX_PUBLIC_TOKEN?.trim()
  const q = query.trim()
  if (!token || q.length < 2) return []

  const path = `${encodeURIComponent(q)}.json`
  const params = new URLSearchParams({
    access_token: token,
    autocomplete: "true",
    limit: "6",
    types: "place,address,poi,locality,neighborhood",
  })

  const res = await fetch(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${path}?${params}`,
    { signal: opts?.signal },
  )
  if (!res.ok) return []

  const data = (await res.json()) as { features?: MapboxFeature[] }
  const out: ResolvedLocation[] = []
  for (const f of data.features ?? []) {
    const r = featureToResolved(f)
    if (r) out.push(r)
  }
  return out
}
